import React from 'react';

function SAHSection() {
  return (
    <section className="py-20 bg-ink-50/60 border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">04 &middot; Extension</p>
        <h2 className="section-title">
          Safety Alignment Hypothesis
          <span className="block text-base md:text-lg font-medium text-ink-500 mt-2 font-sans">
            for Jailbreak / Red-teaming Attacks
          </span>
        </h2>

        <div className="prose-paper max-w-3xl mx-auto mt-10 space-y-5">
          <p>
            <strong>SSAH</strong> was originally proposed to explain safety
            alignment under direct attacks. Our research shows it extends beyond
            direct attacks to provide theoretical guidance for jailbreak and
            red-teaming scenarios.
          </p>
        </div>

        <h3 className="subsection-title text-center">Evolving from SSAH to SAH</h3>
        <div className="prose-paper max-w-3xl mx-auto space-y-5">
          <p>
            Building on SSAH, we propose the{' '}
            <strong>Safety Alignment Hypothesis (SAH)</strong>, refining and
            extending SSAH to cover all stages of model generation &mdash; a
            comprehensive framework for understanding how alignment should
            influence behavior across all interaction steps.
          </p>
        </div>

        {/* SAH callout */}
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="relative rounded-2xl bg-grad-accent-brand p-[1px] shadow-card">
            <div className="rounded-2xl bg-white p-6 md:p-8">
              <div className="flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500" />
                <span className="text-xs font-semibold uppercase tracking-widest text-accent-600">
                  SAH
                </span>
              </div>
              <p className="mt-3 text-lg md:text-xl font-serif italic text-ink-800 leading-relaxed">
                Given an unsafe model capable of fulfilling users' malicious
                requests, safety alignment should teach the model to{' '}
                <strong className="not-italic">choose and maintain</strong> the
                correct reasoning direction at <strong className="not-italic">each generation step</strong>,
                along with simple refusal mechanisms &mdash; allowing the model
                to continuously re-evaluate and re-choose the reasoning
                direction throughout the interaction.
              </p>
            </div>
          </div>
        </div>

        <h3 className="subsection-title text-center">Theoretical Contributions</h3>
        <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {[
            {
              t: 'Per-token safety',
              b: 'SAH offers a framework for maintaining correct reasoning direction across all generated tokens.',
            },
            {
              t: 'Jailbreak mitigation',
              b: 'A conceptual pathway to ensure safety mechanisms persist even under adversarial attempts.',
            },
            {
              t: 'Future roadmap',
              b: 'Bridges existing alignment techniques and their limitations toward more robust and scalable solutions.',
            },
          ].map((c, i) => (
            <div key={c.t} className="card p-5 hover:shadow-card transition">
              <div className="text-xs font-bold text-accent-600">0{i + 1}</div>
              <div className="mt-2 font-semibold text-ink-900 font-serif">{c.t}</div>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default SAHSection;
